import { ClientMsgKind } from '@infiltre/shared';
import { useState } from 'react';
import { selectAliveCount, selectHost, selectRoom } from '@/entities/room';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import { SoundCue, useSound } from '@/shared/lib/audio';
import { DiscussTimer } from '../ui/DiscussTimer';
import { EliminatePicker } from '../ui/EliminatePicker';
import { SpeakingOrder } from '../ui/SpeakingOrder';

type Stage = 'speak' | 'timer' | 'pick';

/** Standalone discussion orchestrator: speak → timer → pick (timer skipped if 0s). */
export const DiscussOrchestrator = () => {
  const playSound = useSound();
  const room = useGameStore(selectRoom);
  const host = useGameStore(selectHost);
  const aliveCount = useGameStore(selectAliveCount);
  const driver = useSessionStore((s) => s.driver);
  const [stage, setStage] = useState<Stage>('speak');

  if (!room || !driver) return null;
  const hostId = host?.id ?? room.players[0]?.id;
  if (!hostId) return null;
  const discussionSeconds = room.options.discussionSeconds;

  const goPick = (): void => {
    playSound(SoundCue.OpenVote);
    setStage('pick');
  };

  const onReadyToVote = (): void => {
    if (discussionSeconds > 0) {
      setStage('timer');
      return;
    }
    goPick();
  };

  const onPick = (targetPlayerId: string): void => {
    driver.send({
      kind: ClientMsgKind.EliminatePlayer,
      actorPlayerId: hostId,
      targetPlayerId,
    });
  };

  if (stage === 'pick') return <EliminatePicker players={room.players} onPick={onPick} />;
  if (stage === 'timer') {
    return (
      <DiscussTimer
        durationSeconds={discussionSeconds}
        onElapsed={goPick}
        onSkip={goPick}
        suspectsAlive={aliveCount}
      />
    );
  }
  return <SpeakingOrder players={room.players} onReadyToVote={onReadyToVote} mode="standalone" />;
};
